import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { AppActions } from 'src/store/AppReducer';
import SearchIcon from '@mui/icons-material/Search';
import { RootStateType } from 'src/store/RootReducer';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { DatePicker } from '@mui/lab';
import RichTextEditor from "./components/RichTextEditor";
import Multiselect from 'multiselect-react-dropdown';
import { createEvent, fetchEventTemplate } from "src/services/EventService";
import { getAllEmployees } from 'src/services/employeeService';
import { getAudiences } from 'src/services/EventService';
import { getEventDetails } from 'src/services/EventService';
import { updateEvent } from 'src/services/EventService';
import TimePickerDropdown from './components/TimePickerDropdown';

const AddEventForm = ({ open, toggle, editEventId }: { open: () => void; toggle: () => void; editEventId?: string | null; }) => {

  const dispatch = useDispatch();
  const TextFieldStyle = {
    paddingTop: 2,
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '13px'
  };

  const LabelStyle = {
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '13px',
    fontWeight: "light",
    variant: "h5"
  };

  const SelectStyle = {
    width: '94%',
    marginLeft: 15,
    marginTop: 5,
    paddingRight: 15,
    fontSize: '13px'
  };

  const ErrorStyle = {
    paddingTop: 5,
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '12px',
    minHeight: '18px',
    color: 'red',
  };

  const generateTimeSlots = (): string[] => {
    const slots: string[] = [];
    for (let h = 0; h < 24; h++) {
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      const period = h < 12 ? "AM" : "PM";
      slots.push(`${hour12.toString().padStart(2, "0")}:00 ${period}`);
      slots.push(`${hour12.toString().padStart(2, "0")}:30 ${period}`);
    }
    return slots;
  };

  const setInitialStartTime = (): string => {
    const slots = generateTimeSlots();
    const now = new Date();
    const totalMinutes = now.getHours() * 60 + now.getMinutes();
    let nearest = slots[0];
    let minDiff = Infinity;

    slots.forEach((slot) => {
      const [time, period] = slot.split(" ");
      const [h, m] = time.split(":").map(Number);
      let hour = period.toUpperCase() === "PM" && h !== 12 ? h + 12 : h;
      if (period.toUpperCase() === "AM" && h === 12) hour = 0;
      const slotMinutes = hour * 60 + m;
      const diff = Math.abs(slotMinutes - totalMinutes);
      if (diff < minDiff) {
        minDiff = diff;
        nearest = slot;
      }
    });
    return nearest;
  };

  const to24HourFormat = (time12h: string): string => {
    const [time, modifier] = time12h.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    if (modifier?.toUpperCase() === "PM" && hours !== 12) {
      hours += 12;
    }
    if (modifier?.toUpperCase() === "AM" && hours === 12) {
      hours = 0;
    }

    const hh = hours?.toString().padStart(2, "0");
    const mm = minutes?.toString().padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const to12HourFormat = (time24h: string): string => {
    let [hours, minutes] = time24h.split(":").map(Number);

    const period = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 === 0 ? 12 : hours % 12;

    const hh = hours12.toString().padStart(2, "0");
    const mm = minutes.toString().padStart(2, "0");

    return `${hh}:${mm} ${period}`;
  };

  const addHalfHour = (time: string) => {
    let [hours, minutes] = to24HourFormat(time).split(":").map(Number);

    minutes += 30;
    if (minutes >= 60) {
      minutes -= 60;
      hours += 1;
    }
    if (hours >= 24) hours -= 24;

    const hhmm12 = to12HourFormat(`${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`);
    return hhmm12;
  }

  const setInitialEndTime = (): string => {
    return addHalfHour(setInitialStartTime());;
  };


  const useAppSelector: TypedUseSelectorHook<RootStateType> = useSelector;
  const { isLoading } = useAppSelector((state) => state.appReducer);
  const { handleSubmit, reset } = useForm<any>();
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const prevOpen = useRef(open);
  const [resetKey, setResetKey] = useState(0);

  const [eventNameError, setEventNameError] = useState('');
  const [subTitleError, setSubTitleError] = useState('');
  const [audienceError, setAudienceError] = useState('');
  const [optionalAudienceError, setOptionalAudienceError] = useState('');
  const [dateError, setDateError] = useState('');
  const [startTimeError, setStartTimeError] = useState('');
  const [endTimeError, setEndTimeError] = useState('');
  const [facilitatorError, setFacilitatorError] = useState('');
  const [locationError, setLocationError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');

  const [eventName, setEventName] = useState('');
  const [subtitle, setSubTitle] = useState('');
  const [selectedAudience, setSelectedAudience] = useState<any[]>([]);
  const [optionalAudience, setOptionalAudience] = useState<any[]>([]);
  const [eventDate, setEventDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<string>(setInitialStartTime()); 
  const [endTime, setEndTime] = useState<string>(setInitialEndTime()); 
  const [facilitator, setFacilitator] = useState<any | null>(null);
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [msTeamsEventId, setMsTeamsEventId] = useState('');

  const [employees, setEmployees] = useState([]);
  const [outlookEmployees, setOutlookEmployees] = useState([]);
  const [outlookGroups, setOutlookGroups] = useState([]);
  const [audiences, setAudiences] = useState([]);

  const isEdit = (editEventId !== '');

  const [isValid, setIsValid] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const images = [
    `${process.env.PUBLIC_URL}/img/Celebrations.png`,
    `${process.env.PUBLIC_URL}/img/Management.png`,
    `${process.env.PUBLIC_URL}/img/Tech_talk.png`,
    `${process.env.PUBLIC_URL}/img/Sports.png`
  ];

  const [selectedTemplate, setSelectedTemplate] = useState(`${process.env.PUBLIC_URL}/img/Placeholder.jpg`);
  const [base64Image, setBase64Image] = useState("");
  const [searchText, setSearchText] = useState('');

  const handleEditorChange = (html: string) => {
    setDescription(html);
  };

  const getEmployeeData = () => {
    getAllEmployees().then((res) => {
      const emps = res?.data;
      setEmployees((emps as any[]) || []);
    });
  };

  const getAudiencesData = () => {
    getAudiences().then((res) => {
      const data = res?.data?.data;

      setOutlookEmployees(data?.users);
      setOutlookGroups(data?.groups);

      const combinedAudiences = [
        ...(data?.users || []),
        ...(data?.groups || []),
      ];

      setAudiences(combinedAudiences);
    });
  };

  const onClose = () => {
    toggle();
  };

  useEffect(() => {
    if (prevOpen.current && !open) {
      resetForm();
      clearErrorMessages();
    }
    prevOpen.current = open;
  }, [open]);

  const resetForm = () => {
    reset();
    setFormSubmitted(false);
    setDeafaultvalues();
    clearErrorMessages();
  };

  const setDeafaultvalues = (() => {
    setEventName('');
    setSubTitle('')
    setSelectedAudience([]);
    setOptionalAudience([]);
    setResetKey(prev => prev + 1);
    setEventDate(null);
    setStartTime(setInitialStartTime());
    setEndTime(setInitialEndTime());
    setFacilitator(null);
    setLocation('');
    setDescription('');
    setBase64Image('');
    setSelectedTemplate(`${process.env.PUBLIC_URL}/img/Placeholder.jpg`);
  })

  //clear inline error messages
  const clearErrorMessages = (() => {
    setEventNameError('');
    setAudienceError('');
    setDateError('');
    setStartTimeError('');
    setEndTimeError('');
    setLocationError('');
    setDescriptionError('');
  })

  const isEmpty = (val: string | null | undefined) => !val || val.trim() === '';

  const checkValidations = () => {
    const IsEventNameValid = validateEventName();
    const IsAudienceValid = validateAudience();
    const IsEventDateValid = validateEventDate();
    const IsStartTimeValid = validateStartTime();
    const IsEndTimeValid = validateEndTime();
    const IsLocationValid = validateLocation();
    const IsDescription = validateDescription();

    const isFormValid = (IsEventNameValid && IsAudienceValid && IsEventDateValid &&
      IsStartTimeValid && IsEndTimeValid && IsLocationValid && IsDescription)

    setIsValid(isFormValid);
    return isFormValid
  }

  useEffect(() => {
    if (formSubmitted) {
      checkValidations();
    }
  }, [eventName,subtitle, selectedAudience, optionalAudience, eventDate, startTime, endTime, facilitator, location, description]);

  const validateEventName = (): boolean => {
    if (isEmpty(eventName)) {
      setEventNameError('Event name is required.');
      return false;
    } if (eventName.length < 3) {
      setEventNameError("Event Name must be more than 3 characters.");
      return false;
    } else if (eventName.length > 255) {
      setEventNameError("Event Name must be less than 255 characters.");
      return false;
    }
    setEventNameError('');
    return true;
  };

  const validateAudience = (): boolean => {
    if (!selectedAudience || selectedAudience.length <= 0) {
      setAudienceError('At least one attendee or group must be selected.');
      return false;
    }
    setAudienceError('');
    return true;
  };

  const validateEventDate = (): boolean => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (!eventDate) {
      setDateError('Event date is required.');
      return false;
    }
    if (eventDate instanceof Date) {
      if (isNaN(eventDate.getTime())) {
        setDateError('Event date is invalid.');
        return false;
      }
    }
    if (eventDate < yesterday) {
      setDateError('Event date cannot be in the past.');
      return false;
    }
    setDateError('');
    return true;
  };

  const isToday = (eventDate: Date | null) => {
    if (!eventDate) return false;
    const date = eventDate instanceof Date ? eventDate : new Date(eventDate);
    const today = new Date();
    return date.getFullYear() === today.getFullYear() &&
          date.getMonth() === today.getMonth() &&
          date.getDate() === today.getDate();
  };

  const convertTimeFrom24To12 = (time24h: string): string => {
    const hhmm = time24h.split(":").slice(0, 2).join(":");
    return to12HourFormat(hhmm);
  };

  const convertTimeFrom12To24 = (time12h: string): string => {
    const [time, modifier] = time12h.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    if (modifier.toUpperCase() === "PM" && hours < 12) {
      hours += 12;
    }
    if (modifier.toUpperCase() === "AM" && hours === 12) {
      hours = 0;
    }

    const hh = hours.toString().padStart(2, "0");
    const mm = minutes.toString().padStart(2, "0");

    return `${hh}:${mm}`;
  };

  const isValidTimeFormat = (time: string): boolean => {
    const cleaned = time.replace(/\s+/g, "");
    return /^([1-9]|0[1-9]|1[0-2]):([0-5][0-9])(AM|PM)$/i.test(cleaned);
  };

  const validateStartTime = (): boolean => {
    if (!startTime) {
      setStartTimeError('Start Time is required.');
      return false;
    }
    if (!isValidTimeFormat(startTime)) {
      setStartTimeError('Invalid time (e.g. 02:30 PM)');
      return false;
    }

    if (isToday(eventDate)) {
      const [hours, minutes] = convertTimeFrom12To24(startTime).split(':').map(Number);
      const now = new Date();
      if (hours < now.getHours() || (hours === now.getHours() && minutes <= now.getMinutes())) {
        setStartTimeError('Start Time cannot be in the past.');
        return false;
      }
    }
    setStartTimeError('');
    return true;
  };

  const validateEndTime = (): boolean => {
    if (!endTime) {
      setEndTimeError('End Time is required');
      return false;
    }
    if (convertTimeFrom12To24(endTime) <= convertTimeFrom12To24(startTime)) {
      setEndTimeError('End Time must be after Start Time.');
      return false;
    }
    if (!isValidTimeFormat(startTime)) {
      setStartTimeError('Invalid time (e.g. 02:30 PM)');
      return false;
    }
    if (isToday(eventDate)) {
      const [hours, minutes] = convertTimeFrom12To24(endTime).split(':').map(Number);
      const now = new Date();
      if (hours < now.getHours() || (hours === now.getHours() && minutes <= now.getMinutes())) {
        setEndTimeError('End Time cannot be in the past.');
        return false;
      }
    }
    setEndTimeError('');
    return true;
  };

  const validateLocation = (): boolean => {
    if (isEmpty(location)) {
      setLocationError('Location is required.');
      return false;
    }
    setLocationError('');
    return true;
  };

  const isEffectivelyEmpty = (() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(description, 'text/html');
    const text = doc.body.textContent || '';
    return text.trim() === '';
  })();

  const validateDescription = (): boolean => {
    if (isEmpty(description) || isEffectivelyEmpty) {
      setDescriptionError('Description is required');
      return false;
    }
    setDescriptionError('');
    return true;
  };

  const onSubmit = () => {
    checkValidations();
    const isFormValid = checkValidations();
    if (!isFormValid) return;

    dispatch(AppActions.showLoader());
    const user = JSON.parse(localStorage.getItem('user') || '{}')

    const selectedEmployees = selectedAudience.filter(
      (item) => outlookEmployees.some((emp) => emp.id === item.id));
    const selectedGroups = selectedAudience.filter(
      (item) => outlookGroups.some((emp) => emp.id === item.id));

    const selectedOptionalEmployees = optionalAudience.filter(
      (item) => outlookEmployees.some((emp) => emp.id === item.id));
    const selectedOptionalGroups = optionalAudience.filter(
      (item) => outlookGroups.some((emp) => emp.id === item.id));

    const audienceString = [
      ...(selectedGroups?.map((item) => item.displayName) || []),
      ...(selectedEmployees?.map((item) => item.displayName) || [])
    ].join(", ");

    const body = {
      ...(isEdit && { id: editEventId }),
      eventName: eventName,
      date: eventDate instanceof Date ? eventDate.getFullYear() + '-' + String(eventDate.getMonth() + 1).padStart(2, '0') + '-' + String(eventDate.getDate()).padStart(2, '0') : eventDate,
      audience: audienceString,
      facilitator: facilitator?.name,
      facilitatorEmail: facilitator ? facilitator.email : user.Email,
      venue: location,
      description: description,
      timeFrom: to24HourFormat(startTime),
      timeTo: to24HourFormat(endTime),
      msTeamsEventId: msTeamsEventId,
      inviteUserEmails: selectedEmployees.map((item) => item.email),
      groupIds: selectedGroups.map((item) => item.id),
      optionalInviteUserEmails: selectedOptionalEmployees.map((item) => item.email),
      optionalGroupIds: selectedOptionalGroups.map((item) => item.id),
      reminderMinutes: 15,
      subtitle:subtitle,
      templateName: getTemplateName(selectedTemplate)
    };

    if (isEdit) {
      updateEvent(body)
        .then(() => {
          reset();
          setSnackBarMessage('Event updated successfully!');
          setSnackbarSeverity('success');
          setSnackBarOpen(true);
          dispatch(AppActions.hideLoader());
          onClose();
          toggle();
        })
        .catch((error) => {
          dispatch(AppActions.hideLoader());
          setSnackBarMessage('Unable to updated the event.');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
        });
    } else {
      createEvent(body)
        .then(() => {
          reset();
          setSnackBarMessage('You have created a new event successfully.');
          setSnackbarSeverity('success');
          setSnackBarOpen(true);
          dispatch(AppActions.hideLoader());
          onClose();
          toggle();
        })
        .catch((error) => {
          dispatch(AppActions.hideLoader());
          setSnackBarMessage('Unable to create a new event');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
        });
    }
  };

  const getEvent = () => {
    dispatch(AppActions.showLoader());
    getEventDetails(editEventId)
      .then(async (response) => {
        reset();
        const res = response?.data?.data;
        setEventName(res.eventName);
        setSubTitle(res.subtitle);
        const selectedGroups = outlookGroups.filter((group) =>
          res.groupIds.includes(group.id)
        );
        const selectedEmployees = outlookEmployees.filter((emp) =>
          res.inviteUserEmails.includes(emp.email)
        );
        setSelectedAudience([...selectedGroups, ...selectedEmployees]);

        const optionalSelectedGroups = outlookGroups.filter((group) =>
          res.optionalGroupIds.includes(group.id)
        );
        const optionalSelectedEmployees = outlookEmployees.filter((emp) =>
          res.optionalInviteUserEmails.includes(emp.email)
        );
        setOptionalAudience([...optionalSelectedGroups, ...optionalSelectedEmployees]);
        setResetKey(prev => prev + 1);

        setEventDate(res.date);
        setStartTime(convertTimeFrom24To12(res.timeFrom));
        setEndTime(convertTimeFrom24To12(res.timeTo));
        setFacilitator(employees.find((option) => containsText(option.name, res.facilitator)));
        setLocation(res.venue);
        setDescription(res.description);
        setMsTeamsEventId(res.msTeamsEventId)
        setSelectedTemplate(res.templateName)
        dispatch(AppActions.hideLoader());
      })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage('Unable to get event details');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
    dispatch(AppActions.hideLoader());
  };

  useEffect(() => {
    if (editEventId) {
      getEvent();
    }
  }, [editEventId]);

  useEffect(() => {
    getEmployeeData();
    getAudiencesData();
  }, []);

  const containsText = (text, searchText) =>
    text.toLowerCase().includes(searchText?.toLowerCase());

  const filteredEmployees = useMemo(() => {
    return employees.length > 0
      ? employees.filter((option) => containsText(option.name, searchText))
      : [];
  }, [searchText, employees]);

  const handleStartTimeChange = (val: string) => {
    setStartTime(val);

    if (!val || !isValidTimeFormat(val)) {
      setEndTime("");
    } else {
      setEndTime(addHalfHour(val));
    }
  };

  const handleEndTimeChange = (val: string) => {
    if (!val) return;
    setEndTime(val);
  };

  const checkTemplateValidations = () =>{
    const IsEventNameValid = validateEventName();
    const IsAudienceValid = validateAudience();
    const IsEventDateValid = validateEventDate();
    const IsStartTimeValid = validateStartTime();
    const IsEndTimeValid = validateEndTime();
    const IsLocationValid = validateLocation();
   
    const isFormValid =(IsEventNameValid && IsAudienceValid &&
      IsStartTimeValid && IsEndTimeValid && IsLocationValid && IsEventDateValid 
    )

    if (eventName.length>28 || subtitle.length>28 || selectedAudience.length>15 || location.length>20){
      if (eventName.length>28) {
        setSnackBarMessage('Current templates only support titles up to 28 characters.');
      }else if(subtitle.length>28){
        setSnackBarMessage('Current templates only support subtitle up to 28 characters.');
      }else if(selectedAudience.length>15){
        setSnackBarMessage('Current templates only support audiences up to 15 characters.');
      }else if(location.length>20){
        setSnackBarMessage('Current templates only support location up to 20 characters.');
      }
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
      return false;
    }

    setIsValid(isFormValid);
    return isFormValid
  }

  const getTemplateName = (url: string) => {
    if (url.includes("Placeholder.jpg")) return "";
    const parts = url.split("/");
    const filename = parts.pop() || "";
    return filename.replace(".png", "");
  };

  const getEventTemplate = () => {
    const isFormValid = checkTemplateValidations();
    if (!isFormValid) return;

    dispatch(AppActions.showLoader());
    const user = JSON.parse(localStorage.getItem('user') || '{}')

    const selectedEmployees = selectedAudience.filter(
      (item) => outlookEmployees.some((emp) => emp.id === item.id));
    const selectedGroups = selectedAudience.filter(
      (item) => outlookGroups.some((emp) => emp.id === item.id));

    const audienceString = [
      ...(selectedGroups?.map((item) => item.displayName) || []),
      ...(selectedEmployees?.map((item) => item.displayName) || [])
    ].join(", ");

    const body = {
      title: eventName,
      subtitle: subtitle,
      date: eventDate instanceof Date ? eventDate.toISOString().split("T")[0] : eventDate,
      startTime: startTime,
      endTime: endTime,
      location: location,
      facilitator: facilitator?.name,
      audience: audienceString,
      templateName: getTemplateName(selectedTemplate)
    };
    
    fetchEventTemplate(body)
      .then(async (response) => {
        setBase64Image(response.data.data);
        dispatch(AppActions.hideLoader());
      })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage('Unable to get event template');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
  };

  useEffect(() => {
    if (!selectedTemplate.endsWith("/img/Placeholder.jpg")) {
      getEventTemplate();
    }
  }, [selectedTemplate]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Grid item xs={12} sx={{ pl: 2, ml: 2, pt: '10px !important' }} >
            <Typography style={{ fontSize: '15px' }} variant="h3">
              {editEventId ? 'Edit Event' : 'Create New Event'}
            </Typography>
          </Grid>

          <Grid item xs={5}>
            <Grid container spacing={2} sx={{ '& .MuiGrid-item': { pt: 0 } }}>
              <Grid item xs={12} >
                <Typography style={{ ...LabelStyle, marginTop: '10px' }}>
                  Event Name<span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth error={!!eventNameError}>
                  <TextField
                    value={eventName}
                    inputProps={{ maxLength: 256 }}
                    size="small"
                    style={TextFieldStyle}
                    fullWidth
                    placeholder="Enter Event Name"
                    variant="outlined"
                    onChange={(e) => {
                      setEventName(e.target.value)
                      if (e.target.value.length < 3) {
                        setEventNameError("Event Name must be more than 3 characters.");
                      } else if (e.target.value.length > 255) {
                        setEventNameError("Event Name must be less than 255 characters.");
                      } else {
                        setEventNameError("");
                      }
                    }}
                  />
                </FormControl>

                <Typography style={ErrorStyle}>
                  {eventNameError}
                </Typography>
              </Grid>
              <Grid item xs={12} >
                <Typography style={LabelStyle}>
                  Event subtitle
                </Typography>
                <FormControl fullWidth error={!!subTitleError}>
                  <TextField
                    value={subtitle}
                    inputProps={{ maxLength: 256 }}
                    size="small"
                    style={TextFieldStyle}
                    fullWidth
                    placeholder="Enter Event subtitle"
                    variant="outlined"
                    onChange={(e) => {
                      setSubTitle(e.target.value)
                    }}
                  />
                </FormControl>

                <Typography style={ErrorStyle}>
                  {subTitleError}
                </Typography>
              </Grid>
              <Grid item xs={12} >
                <Typography style={LabelStyle}>
                  Audience<span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth>
                  <Multiselect
                    key={resetKey}
                    displayValue="displayName"
                    options={audiences}
                    selectedValues={selectedAudience}
                    placeholder={selectedAudience?.length === 0 ? 'Search Audience' : ''}
                    onSelect={(selectedList) => {
                      setSelectedAudience(selectedList);
                      const active = document.activeElement as HTMLElement | null;
                      if (active && typeof active.blur === "function") {
                        active.blur();
                      }
                    }}
                    onRemove={(selectedList) => setSelectedAudience(selectedList)}
                    className='user-role-multi-select'
                    style={{
                      multiselectListContainer: {
                        paddingRight: 30,
                      },
                      multiselectContainer: {
                        paddingLeft: 15,
                        paddingRight: 15,
                      },
                      searchBox: {
                        height: '36px',
                        fontSize: '13px',
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        borderRadius: '6px',
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        overflowY: 'auto',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        '&::WebkitScrollbar': {
                          display: 'none',
                        },
                      },
                      optionContainer: {
                        width: '94%',
                        overflowX: 'auto',
                      },
                      chips: {
                        height: '24px',
                        marginBottom: '0px',
                      },
                    }}
                  />
                  <Typography style={ErrorStyle}>
                    {audienceError}
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12} >
                <Typography style={LabelStyle}>
                  Optional Audience
                </Typography>
                <FormControl fullWidth>
                  <Multiselect
                    key={resetKey}
                    displayValue="displayName"
                    options={audiences}
                    selectedValues={optionalAudience}
                    placeholder={optionalAudience.length === 0 ? 'Search Optional Audience' : ''}
                    onSelect={(selectedList) => {
                      setOptionalAudience(selectedList)
                      const active = document.activeElement as HTMLElement | null;
                      if (active && typeof active.blur === "function") {
                        active.blur();
                      }
                    }}
                    onRemove={(selectedList) => setOptionalAudience(selectedList)}
                    className='user-role-multi-select'
                    style={{
                      multiselectListContainer: {
                        paddingRight: 30,
                      },
                      multiselectContainer: {
                        paddingLeft: 15,
                        paddingRight: 15,
                      },
                      searchBox: {
                        height: '36px',
                        fontSize: '13px',
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        borderRadius: '6px',
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        overflowY: 'auto',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        '&::WebkitScrollbar': {
                          display: 'none',
                        },
                      },
                      optionContainer: {
                        width: '94%',
                        overflowX: 'auto',
                      },
                      chips: {
                        height: '24px',
                        marginBottom: '0px',
                      },
                    }}
                  />
                </FormControl>
                <Typography style={{ ...ErrorStyle, minHeight: '10px' }}>
                  {optionalAudienceError}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography style={LabelStyle}>
                  Date<span style={{ color: 'red' }}>*</span>
                </Typography>
                <div style={{ marginLeft: 13, marginRight: 12, marginTop: 2 }}>
                  <FormControl fullWidth>
                    <DatePicker
                      InputProps={{
                        className: 'date-picker',
                      }}
                      renderInput={(params) => (
                        <TextField {...params} />
                      )}
                      value={eventDate}
                      onChange={(newValue: Date | null) => {
                        setEventDate(newValue);
                      }}
                    />
                  </FormControl>
                </div>
                <Typography style={ErrorStyle}>
                  {dateError}
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ pr: 2 }}>
                <Typography style={LabelStyle}>
                  From<span style={{ color: 'red' }}>*</span>
                </Typography>
                <div style={{ marginLeft: 12, marginTop: 2 }}>
                  <FormControl fullWidth>
                    <TimePickerDropdown value={startTime} onChange={handleStartTimeChange} timeSlots={generateTimeSlots()} />
                  </FormControl>
                </div>
                <Typography style={ErrorStyle}>
                  {startTimeError}
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ pr: 2 }}>
                <Typography style={LabelStyle}>
                  To<span style={{ color: 'red' }}>*</span>
                </Typography>
                <div style={{ marginLeft: 12, marginTop: 2 }}>
                  <FormControl fullWidth>
                    <TimePickerDropdown value={endTime} onChange={handleEndTimeChange} timeSlots={generateTimeSlots()} />
                  </FormControl>
                </div>
                <Typography style={ErrorStyle}>
                  {endTimeError}
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ pr: 2 }}>
                <Typography style={LabelStyle}>
                  Facilitator
                </Typography>
                <Select
                  disabled={isEdit}
                  MenuProps={{
                    autoFocus: false,
                    PaperProps: {
                      sx: {
                        ml: '0.25%',
                        width: '11%'
                      }
                    }
                  }}
                  className="select-text"
                  size="small"
                  style={{ ...SelectStyle, marginTop: '3px' }}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                  defaultValue={''}
                  value={facilitator || ''}
                  onChange={(e) => {
                    setFacilitator(e.target.value)
                  }}
                  onClose={() => setSearchText('')}
                  renderValue={(selected) => selected?.name || 'Enter Facilitator'}
                >
                  <ListSubheader>
                    <TextField
                      size="small"
                      autoFocus
                      placeholder="Type to search..."
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        )
                      }}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Escape') {
                          e.stopPropagation();
                        }
                      }}
                    ></TextField>
                  </ListSubheader>
                  <MenuItem style={{ display: 'none' }} selected disabled value={''}>
                    Enter Facilitator
                  </MenuItem>
                  {filteredEmployees.length > 0 &&
                    filteredEmployees?.map((filteredEmployee, i) => (
                      <MenuItem key={i} value={filteredEmployee}
                        sx={{
                          maxWidth: '200px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'block',
                        }}
                      >
                        {filteredEmployee.name}
                      </MenuItem>
                    ))}
                </Select>
                <Typography style={ErrorStyle}>
                  {facilitatorError}
                </Typography>
              </Grid>
              <Grid item xs={6} >
                <Typography style={LabelStyle}>
                  Location<span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  id="customerName"
                  inputProps={{ maxLength: 255 }}
                  size="small"
                  style={{ ...TextFieldStyle, height: '36px', marginTop: '0px' }}
                  fullWidth
                  placeholder="Enter Location Name"
                  value={location}
                  variant="outlined"
                  onChange={(e) => {
                    if (e.target.value.length > 255) {
                      setLocationError('Max character limit reached');
                    } else {
                      setLocation(e.target.value);
                      setLocationError('');
                    }
                  }}
                />
                <Typography style={ErrorStyle}>
                  {locationError}
                </Typography>
              </Grid>
              <Grid item xs={12} >
                <Typography style={LabelStyle} sx={{ pb: 0.5 }}>
                  Description<span style={{ color: 'red' }}>*</span>
                </Typography>
                <RichTextEditor value={description} onChange={handleEditorChange} setError={setDescriptionError} />
                <Typography style={ErrorStyle}>
                  {descriptionError}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={7} sx={{ height: 670}}>
           <Typography variant="h5" fontWeight="light" sx={{ p: 0 }}>
              Preview 
            </Typography>
            <Box
              sx={{
                position: "relative",width: "100%",margin: "0 auto",borderRadius: 2,overflow: "hidden",
                "&:hover .thumbnails": {
                  opacity: 1,
                },
              }}
            >
              
              <Box 
                component="img" src={ base64Image? `data:image/png;base64,${base64Image}`: selectedTemplate}             
                alt="selectedTemplate Image"
                sx={{width: "100%",height: "100%",objectFit: "cover",display: "block",}}
              />

              {selectedTemplate === "/img/Placeholder.jpg" && (
                <Typography
                  variant="body2"
                  fontStyle="italic"
                  color="white"
                  sx={{
                    fontSize: '1.5rem',
                    position: 'absolute',
                    top: '15%',
                    left: '50%',            
                    transform: 'translateX(-50%)', 
                    padding: '4px 12px',
                    borderRadius: 1,
                    zIndex: 2,
                    textAlign: 'center',
                    width: 'max-content',  
                  }}
                >
                  Feel free to choose a template for your event.
                </Typography>

              )}

              <Box
                className="thumbnails"
                sx={{
                  position: "absolute",bottom: 0,left: 0,right: 0,
                  background: "linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0))",
                  padding: 1,transition: "opacity 0.3s",
                  opacity: selectedTemplate === "/img/Placeholder.jpg" ? 1 : 0
                }}
              >
                <Grid container spacing={1} justifyContent="center">
                  {images.map((img, idx) => (
                    <Grid item key={idx}>
                      <Box
                        component="img"
                        src={img}
                        alt={`Thumbnail ${idx}`}
                        onClick={() => {
                          if (selectedTemplate === img) {
                            setBase64Image("");
                            setSelectedTemplate("/img/Placeholder.jpg");
                          } else {
                            setSelectedTemplate(img);
                          }
                        }}
                        sx={{
                          width: 75,objectFit: "cover",borderRadius: 1,cursor: "pointer",
                          transition: "transform 0.3s, box-shadow 0.3s",
                          "&:hover": {
                            transform: "scale(1.1)",
                            boxShadow: 3,
                          },
                          border:selectedTemplate === img ? "2px solid #1976d2": "2px solid transparent",
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </Grid>
          <Grid item sx={{ '&.MuiGrid-item': { pt: 0 }, display: 'flex', justifyContent: 'flex-end' }} xs={12}>
            <Button
              id="addCustomer-btn-onClose"
              sx={{
                width: '10%',
                fontWeight: 'normal',
                background: '#F5F7FF',
                fontSize: '13px'
              }}
              onClick={() => onClose()}
            >
              Cancel
            </Button>
            <Button
              id="addCustomer-btn-onSubmit"
              disabled={isLoading}
              className={isLoading ? 'loading' : ''}
              onClick={() => setFormSubmitted(true)}
              sx={{
                ml: 2,
                width: '10%',
                fontSize: '13px',
                fontWeight: 'normal',
                background: '#0069E4'
              }}
              variant="contained"
              type="submit"
            >
              {isEdit ? "Update" : "Create"}
            </Button>
          </Grid>
        </Grid>
      </form>
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />
    </>

  );
};

export default AddEventForm;